import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DigitsOnlyDirective } from '../../shared/digits-only';
import {
  PartnerKycService,
  PartnerKyc,
  PartnerKycUpsert,
  KycStatus,
} from '../../services/partner-kyc';
import { AuthService } from '../../services/auth';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { DocUploadComponent } from '../../shared/doc-upload';

@Component({
  selector: 'app-partner-kyc',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    DigitsOnlyDirective,
    DocUploadComponent,
  ],
  templateUrl: './partner-kyc.html',
  styleUrls: ['./partner-kyc.css'],
})
export class PartnerKycPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(PartnerKycService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private router = inject(Router);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  loading = signal(false);
  saving = signal(false);
  status = signal<KycStatus>('NotSubmitted');
  rejectionReason = signal<string | null>(null);
  partnerName = signal<string>('');
  partnerCode = signal<string>('');
  private partnerId = 0;

  // Validators mirror dbo.Partner_KYC_Details: column lengths -> maxLength, and document
  // formats -> pattern. Only FullName is NOT NULL in the schema; PAN/Aadhaar/Mobile/Address
  // are kept required as a business rule for KYC (the column is nullable, so this is stricter).
  form: FormGroup = this.fb.group({
    // Company
    fullName: ['', [Validators.required, Validators.maxLength(150)]], // NVARCHAR(150) NOT NULL — company name
    // Documents
    panNumber: [
      '',
      [Validators.required, Validators.maxLength(10), Validators.pattern(/^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/)],
    ], // company PAN — exactly 10, ABCDE1234F
    // Company registration number — stored in the (repurposed) AadhaarNumber column.
    aadhaarNumber: [
      '',
      [Validators.required, Validators.maxLength(30), Validators.pattern(/^[A-Za-z0-9\-/]{5,30}$/)],
    ], // alphanumeric registration/CIN, 5–30 chars
    gstNumber: [
      '',
      [
        Validators.maxLength(15),
        Validators.pattern(/^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[0-9A-Za-z]{1}[Zz]{1}[0-9A-Za-z]{1}$/),
      ],
    ], // exactly 15-char GSTIN, optional
    // Contact
    email: ['', [Validators.email, Validators.maxLength(150)]], // VARCHAR(150) NULL
    mobileNumber: [
      '',
      [Validators.required, Validators.minLength(10), Validators.maxLength(10), Validators.pattern(/^\d{10}$/)],
    ], // exactly 10-digit mobile
    // Address
    addressLine1: ['', [Validators.required, Validators.maxLength(250)]], // NVARCHAR(250)
    addressLine2: ['', [Validators.maxLength(250)]], // NVARCHAR(250)
    city: ['', [Validators.required, Validators.maxLength(100)]], // VARCHAR(100)
    state: ['', [Validators.required, Validators.maxLength(100)]], // VARCHAR(100)
    pincode: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6), Validators.pattern(/^\d{6}$/)]], // exactly 6 digits
    // Bank
    bankName: ['', [Validators.maxLength(150)]], // NVARCHAR(150)
    accountHolderName: ['', [Validators.maxLength(150)]], // NVARCHAR(150)
    accountNumber: ['', [Validators.maxLength(18), Validators.pattern(/^\d{6,18}$/)]], // 6–18 digits
    ifscCode: ['', [Validators.maxLength(11), Validators.pattern(/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/)]], // exactly 11 — HDFC0001234
    branchName: ['', [Validators.maxLength(150)]], // NVARCHAR(150)
    // Uploads (server paths/URLs for now) — NVARCHAR(500)
    panFilePath: ['', [Validators.maxLength(500)]],
    aadhaarFilePath: ['', [Validators.maxLength(500)]],
    gstFilePath: ['', [Validators.maxLength(500)]],
    cancelledChequePath: ['', [Validators.maxLength(500)]],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    // An admin shouldn't be on this page.
    if (this.auth.isAdmin()) {
      this.router.navigate(['/dashboard']);
      return;
    }
    this.load();
  }

  get isApproved() {
    return this.status() === 'Approved';
  }
  get isPending() {
    return this.status() === 'Pending';
  }
  get isRejected() {
    return this.status() === 'Rejected';
  }

  // ===================== load =====================
  load() {
    this.loading.set(true);
    this.svc.loadMyKyc().subscribe({
      next: (kyc: PartnerKyc) => {
        this.loading.set(false);
        this.status.set(kyc.kycStatus);
        this.rejectionReason.set(kyc.rejectionReason ?? null);
        this.partnerId = kyc.partnerId;
        this.partnerName.set(kyc.partnerName ?? '');
        this.partnerCode.set(kyc.partnerCode ?? '');
        // Pre-fill the form from any existing row.
        this.form.patchValue({
          fullName: kyc.fullName ?? '',
          panNumber: (kyc.panNumber ?? '').toUpperCase(),
          aadhaarNumber: (kyc.aadhaarNumber ?? '').toUpperCase(), // company registration no.
          gstNumber: (kyc.gstNumber ?? '').toUpperCase(),
          email: kyc.email ?? '',
          mobileNumber: (kyc.mobileNumber ?? '').replace(/\D/g, ''),
          addressLine1: kyc.addressLine1 ?? '',
          addressLine2: kyc.addressLine2 ?? '',
          city: kyc.city ?? '',
          state: kyc.state ?? '',
          pincode: kyc.pincode ?? '',
          bankName: kyc.bankName ?? '',
          accountHolderName: kyc.accountHolderName ?? '',
          accountNumber: kyc.accountNumber ?? '',
          ifscCode: kyc.ifscCode ?? '',
          branchName: kyc.branchName ?? '',
          panFilePath: kyc.panFilePath ?? '',
          aadhaarFilePath: kyc.aadhaarFilePath ?? '',
          gstFilePath: kyc.gstFilePath ?? '',
          cancelledChequePath: kyc.cancelledChequePath ?? '',
        });
        // Editable only while the partner can act: NotSubmitted (first time) or
        // Rejected (fix & resubmit). Locked once Pending (under review) or Approved.
        if (this.isApproved || this.isPending) this.form.disable();
        else this.form.enable();
      },
      error: () => this.loading.set(false),
    });
  }

  // ===================== submit =====================
  submit() {
    if (this.isApproved || this.isPending) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toast.warning('Please fix the highlighted fields before submitting');
      return;
    }

    const v = this.form.getRawValue();
    // PAN / IFSC / GST are stored uppercase — the input only styles them visually.
    const up = (s: any): string | null => (s ? String(s).trim().toUpperCase() : null);
    const body: PartnerKycUpsert = {
      partnerId: this.partnerId,
      fullName: v.fullName,
      panNumber: up(v.panNumber),
      aadhaarNumber: up(v.aadhaarNumber),
      gstNumber: up(v.gstNumber),
      email: v.email || null,
      mobileNumber: v.mobileNumber,
      addressLine1: v.addressLine1,
      addressLine2: v.addressLine2 || null,
      city: v.city,
      state: v.state,
      pincode: v.pincode,
      bankName: v.bankName || null,
      accountHolderName: v.accountHolderName || null,
      accountNumber: v.accountNumber || null,
      ifscCode: up(v.ifscCode),
      branchName: v.branchName || null,
      panFilePath: v.panFilePath || null,
      aadhaarFilePath: v.aadhaarFilePath || null,
      gstFilePath: v.gstFilePath || null,
      cancelledChequePath: v.cancelledChequePath || null,
    };

    this.saving.set(true);
    this.svc.upsert(body).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to submit KYC');
          return;
        }
        this.toast.success(res?.message || 'KYC submitted — pending admin review');
        // Any submit resets the server status to Pending.
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  goToApp() {
    this.router.navigate(['/partner-dashboard']);
  }
}
